import { supabase } from './supabase'
import type { Database } from '@/types/database-extensions'
import { handleApiError } from '@/utils/error-handlers'
import type { RecurrencePattern } from '@/types/event-types'
import { EventFilters } from '@/types/event-types'

// Import specialized functionality
import { createRecurringEvents } from './recurring-events'
import {
  joinEvent,
  leaveEvent,
  getEventParticipants,
  getEventAttendanceStats,
  isEventSignupAllowed,
  type EventParticipant,
} from './event-participants'
import { createGame, searchGames, getGameById, type Game } from './games'
import { subscribeToEvents, subscribeToEventParticipants } from './event-subscriptions'

// Export common event types
export type Event = Database['public']['Tables']['events']['Row'] & {
  game?: Game // Add the optional game property to fix type error
}
export type EventInsert = Database['public']['Tables']['events']['Insert']
export type EventUpdate = Database['public']['Tables']['events']['Update']

// Re-export imported types
export type { EventParticipant, Game }

/**
 * Creates a new event, handling both recurring and single events
 * @param eventData - The event data to create
 * @returns Object containing the created event data or an error
 */
export async function createEvent(eventData: EventInsert) {
  try {
    // Handle recurring events
    if (eventData.is_recurring === true && eventData.recurrence_pattern) {
      // Type assertion to address compatibility issue
      return createRecurringEvents({
        ...eventData,
        is_recurring: true,
        recurrence_pattern: eventData.recurrence_pattern as RecurrencePattern,
      })
    }

    // Regular single event creation
    const { data, error } = await supabase
      .from('events')
      .insert(eventData)
      .select('*, metadata')
      .single()

    if (error) throw new Error(`Failed to create event: ${error.message}`)

    return { data, error: null }
  } catch (error) {
    return handleApiError(error, 'creating the event')
  }
}

/**
 * Updates an existing event
 * @param id - The ID of the event to update
 * @param updates - The data to update
 * @returns Object containing the updated event data or an error
 */
export async function updateEvent(id: string, updates: EventUpdate) {
  try {
    const { data, error } = await supabase
      .from('events')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw new Error(`Failed to update event: ${error.message}`)

    return { data, error: null }
  } catch (error) {
    return handleApiError(error, 'updating the event')
  }
}

/**
 * Deletes an event
 * @param id - The ID of the event to delete
 * @returns Object indicating success or an error
 */
export async function deleteEvent(id: string) {
  try {
    const { error } = await supabase.from('events').delete().eq('id', id)

    if (error) throw new Error(`Failed to delete event: ${error.message}`)

    return { success: true, error: null }
  } catch (error) {
    return handleApiError(error, 'deleting the event')
  }
}

/**
 * Gets an event by ID with related data
 * @param id - The ID of the event to retrieve
 * @returns Object containing the event data with relationships or an error
 */
export async function getEventById(id: string) {
  try {
    const { data, error } = await supabase
      .from('events')
      .select(
        `
        *,
        host:profiles!events_host_id_fkey(id, username, full_name, avatar_url),
        participants:event_participants(
          id,
          profile_id,
          status,
          profiles(id, username, full_name, avatar_url)
        ),
        meetup:meetups(id, title, start_time, end_time)
      `
      )
      .eq('id', id)
      .single()

    if (error) throw new Error(`Failed to fetch event: ${error.message}`)

    // If the event has a game_id, fetch the game data separately
    if (data && data.game_id) {
      const { data: gameData, error: gameError } = await getGameById(data.game_id)

      if (!gameError && gameData) {
        // Type assertion to ensure type compatibility
        ;(data as Event).game = gameData
      }
    }

    return { data, error: null }
  } catch (error) {
    return handleApiError(error, 'fetching the event')
  }
}

/**
 * Gets events with optional filtering
 * @param filters - Optional filters to apply to the query
 * @returns Object containing the filtered events or an error
 */
export async function getEvents(
  filters: EventFilters = {}
): Promise<{ data: Event[] | null; count: number; error: null | string }> {
  try {
    const {
      hostId,
      participantId,
      startDate,
      endDate,
      status,
      limit = 20,
      offset = 0,
      query,
      skillLevel,
      isPrivate,
      gameId,
      meetupId,
    } = filters

    // Build the query
    let dbQuery = supabase.from('events').select(
      `
        *,
        host:profiles!events_host_id_fkey(id, username, full_name, avatar_url),
        participant_count:event_participants(count),
        meetup:meetups(id, title)
      `,
      { count: 'exact' }
    )

    // Apply filters
    if (hostId) dbQuery = dbQuery.eq('host_id', hostId)

    if (participantId) {
      const { data: participantEvents } = await supabase
        .from('event_participants')
        .select('event_id')
        .eq('profile_id', participantId)

      if (participantEvents && participantEvents.length > 0) {
        const eventIds = participantEvents.map(pe => pe.event_id)
        dbQuery = dbQuery.in('id', eventIds)
      } else {
        return { data: [], count: 0, error: null }
      }
    }

    if (startDate) {
      const startDateStr = startDate instanceof Date ? startDate.toISOString() : startDate
      dbQuery = dbQuery.gte('start_time', startDateStr)
    }

    if (endDate) {
      const endDateStr = endDate instanceof Date ? endDate.toISOString() : endDate
      dbQuery = dbQuery.lte('start_time', endDateStr)
    }

    // Apply remaining filters
    if (status) dbQuery = dbQuery.eq('status', status)
    if (query && query.trim() !== '') {
      dbQuery = dbQuery.or(
        `title.ilike.%${query}%,description.ilike.%${query}%,location.ilike.%${query}%`
      )
    }
    if (skillLevel) dbQuery = dbQuery.eq('skill_level', skillLevel)
    if (isPrivate !== undefined) dbQuery = dbQuery.eq('is_private', isPrivate)
    if (gameId) dbQuery = dbQuery.eq('game_id', gameId)
    if (meetupId) dbQuery = dbQuery.eq('meetup_id', meetupId)

    // Apply pagination and ordering
    dbQuery = dbQuery.range(offset, offset + limit - 1).order('start_time', { ascending: true })

    const { data, error, count } = await dbQuery

    if (error) throw new Error(`Failed to fetch events: ${error.message}`)

    // Fetch game data if needed
    if (data?.length > 0) {
      await enrichEventsWithGameData(data)
    }

    return { data, count: Number(count), error: null }
  } catch (error) {
    return { data: null, count: 0, error: handleApiError(error, 'fetching events').error }
  }
}

/**
 * Enriches event data with game information
 * @param events - Array of events to enrich with game data
 */
async function enrichEventsWithGameData(events: Event[]) {
  try {
    // Get unique game IDs from events
    const gameIds = events
      .filter(event => event.game_id)
      .map(event => event.game_id as string)
      .filter((value, index, self) => self.indexOf(value) === index) // Get unique values

    if (gameIds.length === 0) return

    // Fetch games data
    const { data: gamesData } = await supabase.from('games').select('*').in('id', gameIds)

    if (gamesData && gamesData.length > 0) {
      // Create a map of game data by ID for quick lookup
      const gamesMap = gamesData.reduce<Record<string, Game>>((map, game) => {
        map[game.id] = game
        return map
      }, {})

      // Add game data to each event
      events.forEach(event => {
        if (event.game_id && gamesMap[event.game_id]) {
          event.game = gamesMap[event.game_id]
        }
      })
    }
  } catch (error) {
    console.error('Error enriching events with game data:', error)
    // Non-critical operation, so just log the error
  }
}

// Re-export all event-related functionality
export {
  // Specialized event functions
  createRecurringEvents,

  // Participant functions
  joinEvent,
  leaveEvent,
  getEventParticipants,
  getEventAttendanceStats,
  isEventSignupAllowed,

  // Game functions
  createGame,
  searchGames,
  getGameById,

  // Subscription functions
  subscribeToEvents,
  subscribeToEventParticipants,
}
